import { useEffect, useState } from 'react';
import isEqual from 'lodash.isequal';
import { ResponseError, SelectItemProps } from '@ui/src';
import { useAppDispatch } from '@store';
import { Balance, OrganizationType } from '../../types';
import {
  useBlockCustomerAccountCreditMutation,
  useBlockCustomerAccountDebitMutation,
  useCloseCustomerAccountMutation,
  useCreateCustomerAccountMutation,
  useLazyGetAccountPrimariesQuery,
  useLazyGetStatementItemsPdfQuery,
  useOpenCustomerAccountMutation,
  useUnblockCustomerAccountCreditMutation,
  useUnblockCustomerAccountDebitMutation,
  useUpdateCustomerAccountVisibilityMutation,
  useUpdateCustomersOverdraftBalanceValueMutation,
} from '../../../customers/store/services/customers';
import { GeneralInfo } from '../../../individual/types';
import { CorporateGeneralInfo } from '../../../corporate/types';
import getPeriodDateString from '../../helpers/get-period-string';
import { setNotificationVisible } from '../../../../../../../store/slice';

const useCustomerAccounts = ({
  customerId,
  baseInformation,
  type,
}: {
  customerId?: string;
  baseInformation?: Partial<GeneralInfo> | Partial<CorporateGeneralInfo>;
  type: OrganizationType;
}) => {
  const [popupVisible, setPopupVisible] = useState(false);
  const [closeOpenError, setCloseOpenError] = useState('');
  const [blockUblockTopupError, setBlockUblockTopupError] = useState('');
  const [blockUblockWithdrawalsError, setBlockUblockWithdrawalsError] =
    useState('');
  const [topUpPanelIsOpen, setTopUpPanelIsOpen] = useState(false);
  const [selectedTopUpId, setSelectedTopUpId] = useState('');
  const [showCurrencies, setShowCurrencies] = useState(false);
  const [statementPanelVisible, setStatementPanelVisible] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState<Date[]>([]);
  const [selectedFormat, setSelectedFormat] = useState<string>('csv');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [currenciesList, setCurrenciesList] = useState<SelectItemProps[]>([]);
  const [statementError, setStatementError] = useState('');
  const [selectedDateType, setSelectedDateType] = useState('today');
  const [primariesAccounts, setPrimariesAccounts] = useState<SelectItemProps[]>(
    [],
  );
  const [selectedPrimaryAccount, setSelectedPrimaryAccount] = useState('');
  const [visibleToClient, setVisibleToClient] = useState(true);
  const [accountBalances, setBalances] = useState<Balance[] | undefined>(
    undefined,
  );
  const [defaultBalances, setDefaultBalances] = useState<Balance[] | undefined>(
    undefined,
  );

  const dispatch = useAppDispatch();

  const [getAccountPrimaries] = useLazyGetAccountPrimariesQuery();

  const [
    blockDebit,
    { isLoading: blockDebitInProgress, isSuccess: blockDebitIsSuccess },
  ] = useBlockCustomerAccountDebitMutation();
  const [
    unblockDebit,
    { isLoading: unblockDebitInProgress, isSuccess: unblockDebitIsSuccess },
  ] = useUnblockCustomerAccountDebitMutation();
  const [
    blockCredit,
    { isLoading: blockCreditInProgress, isSuccess: blockCreditIsSuccess },
  ] = useBlockCustomerAccountCreditMutation();
  const [
    unblockCredit,
    { isLoading: unblockCreditInProgress, isSuccess: unblockCreditIsSuccess },
  ] = useUnblockCustomerAccountCreditMutation();
  const [close, { isLoading: closeInProgress, isSuccess: closeIsSuccess }] =
    useCloseCustomerAccountMutation();
  const [open, { isLoading: openInProgress, isSuccess: openIsSuccess }] =
    useOpenCustomerAccountMutation();
  const [
    createIndividualAccount,
    { isLoading: createAccountInProgress, isSuccess: createAccountSuccess },
  ] = useCreateCustomerAccountMutation();
  const [getCustomersStatementPdf, { isLoading, isFetching, isSuccess }] =
    useLazyGetStatementItemsPdfQuery();

  const [updateVisibility, { isLoading: updateVisiblityInProgress }] =
    useUpdateCustomerAccountVisibilityMutation();

  const [
    updateOverdraftBalanceValue,
    { isLoading: updateOverdraftBalanceIsLoading },
  ] = useUpdateCustomersOverdraftBalanceValueMutation();

  const onUpdateOverdraftBalanceValue = async (accountId: string) => {
    if (!isEqual(defaultBalances, accountBalances)) {
      const updatedBalances: Balance[] = [];

      accountBalances?.forEach((item) => {
        const defaultBalance = defaultBalances?.find(
          (balance) => balance.id === item.id,
        );

        if (defaultBalance && !isEqual(defaultBalance, item)) {
          updatedBalances.push(item);
        }
      });

      if (updatedBalances.length) {
        const responses = await Promise.all(
          updatedBalances?.map((item) => {
            const response = updateOverdraftBalanceValue({
              id: customerId as string,
              accountId,
              balanceId: item.id,
              overdraftEnabled: item.overdraftEnabled as boolean,
              overdraftValue: item.overdraftValue as number,
            });

            return response;
          }),
        );

        if (responses) {
          const nextDefaultBalances = defaultBalances?.map((item) => {
            let result = { ...item };

            const currentUpdatedBalance = updatedBalances?.find(
              (balance) => balance.id === item.id,
            );

            if (currentUpdatedBalance) {
              result = currentUpdatedBalance;
            }

            return result;
          });

          setDefaultBalances(nextDefaultBalances);
        }
      }
    }
  };

  const onUpdateVisibility = async ({
    id,
    visibleToClientValue,
  }: {
    id: string;
    visibleToClientValue: boolean;
  }) => {
    await updateVisibility({
      id,
      body: {
        visibleToClient: visibleToClientValue,
        id,
      },
    });
  };

  const onClose = async ({
    id,
    closed,
    comment,
  }: {
    id: string;
    closed: boolean;
    comment: string;
  }) => {
    setCloseOpenError('');

    if (closed) {
      const response = (await close({
        id: customerId as string,
        accountId: id,
        body: {
          comment,
        },
      })) as { error?: { data: { message: string[] } } };

      if (response.error?.data?.message && response.error?.data?.message[0]) {
        setCloseOpenError(response.error?.data?.message[0]);
      }
    } else {
      const response = (await open({
        id: customerId as string,
        accountId: id,
        body: {
          comment,
        },
      })) as { error?: { data: { message: string[] } } };

      if (response.error?.data?.message && response.error?.data?.message[0]) {
        setCloseOpenError(response.error?.data?.message[0]);
      }
    }
  };

  const onBlockTopup = async ({
    id,
    creditBlock,
  }: {
    id: string;
    creditBlock: boolean;
  }) => {
    setBlockUblockTopupError('');

    if (creditBlock) {
      const response = (await blockCredit({
        accountId: id,
        id: customerId as string,
      })) as { error?: { data: { message: string[] } } };

      if (response.error?.data?.message && response.error?.data?.message[0]) {
        setBlockUblockTopupError(response.error?.data?.message[0]);
      }
    } else {
      const response = (await unblockCredit({
        accountId: id,
        id: customerId as string,
      })) as { error?: { data: { message: string[] } } };

      if (response.error?.data?.message && response.error?.data?.message[0]) {
        setBlockUblockTopupError(response.error?.data?.message[0]);
      }
    }
  };

  const onBlockWithdraw = async ({
    id,
    debitBlock,
  }: {
    id: string;
    debitBlock: boolean;
  }) => {
    setBlockUblockWithdrawalsError('');

    if (debitBlock) {
      const response = (await blockDebit({
        accountId: id,
        id: customerId as string,
      })) as { error?: { data: { message: string[] } } };

      if (response.error?.data?.message && response.error?.data?.message[0]) {
        setBlockUblockWithdrawalsError(response.error?.data?.message[0]);
      }
    } else {
      const response = (await unblockDebit({
        accountId: id,
        id: customerId as string,
      })) as { error?: { data: { message: string[] } } };

      if (response.error?.data?.message && response.error?.data?.message[0]) {
        setBlockUblockWithdrawalsError(response.error?.data?.message[0]);
      }
    }
  };

  const onCreateAccount = () => {
    const clientName =
      type === OrganizationType.INDIVIDUAL
        ? `${(baseInformation as Partial<GeneralInfo>)?.firstName} ${(baseInformation as Partial<GeneralInfo>)?.lastName}`
        : (baseInformation as Partial<CorporateGeneralInfo>)?.name;

    const body: {
      description: string;
      primaryId?: string;
      visibleToClient: boolean;
    } = {
      description: clientName as string,
      visibleToClient,
    };

    if (selectedPrimaryAccount) {
      body.primaryId = selectedPrimaryAccount;
    }

    createIndividualAccount({
      organizationId: customerId as string,
      body,
    });
  };

  const onCloseStatementPanel = () => {
    setSelectedAccount('');
    setSelectedCurrency('');
    setSelectedDateRange([]);
    setCurrenciesList([]);
    setStatementError('');
    setStatementPanelVisible(false);
    setSelectedDateType('today');
    setSelectedFormat('csv');
  };

  const onDownloadStatement = async () => {
    const { dateString, dateFromValue, dateToValue } = getPeriodDateString(
      selectedDateType,
      selectedDateRange,
    );

    const datePeriod = `from ${new Date(
      dateFromValue as Date,
    ).toLocaleDateString()} to ${new Date(
      dateToValue as Date,
    ).toLocaleDateString()}`;

    const baseQuery = `accountId=${selectedAccount}&currencyId=${selectedCurrency}&${dateString}`;

    const currentAccountNumber = baseInformation?.accounts?.find(
      (item) => item.id === selectedAccount,
    )?.fullNumber;

    const response = (await getCustomersStatementPdf({
      query: baseQuery,
      type: selectedFormat,
    })) as {
      error: ResponseError;
      data: any;
    };

    if (response.data) {
      const hiddenElement = document.createElement('a');
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = `Statements ${
        currenciesList.find((item) => item.value === selectedCurrency)?.name
      } #${currentAccountNumber} ${datePeriod}.${selectedFormat}`;
      hiddenElement.click();
    } else if (response.error) {
      if (response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0].property === 'coreError'
        ) {
          setStatementError(response.error.data.message[0].value as string);
        } else if (typeof response.error.data.message === 'string') {
          setStatementError(response.error.data.message);
        } else {
          setStatementError('Internal Server Error. Try Again');
        }
      }
    }
  };

  const getAccountPrimariesAction = async () => {
    const response = await getAccountPrimaries();

    if (response) {
      setPrimariesAccounts(
        response?.data?.data.edges?.map((item) => ({
          name: item.node.description,
          value: item.node.id,
        })) as SelectItemProps[],
      );
    }
  };

  useEffect(() => {
    getAccountPrimariesAction();
  }, []);

  useEffect(() => {
    if (createAccountSuccess) {
      setPopupVisible(false);
      setSelectedPrimaryAccount('');
      setVisibleToClient(true);

      dispatch(
        setNotificationVisible({
          visible: true,
          text: "Account creation in progress. You'll be notified.",
          type:
            type === OrganizationType.INDIVIDUAL
              ? 'individual-client-account-create'
              : 'corporate-client-account-create',
        }),
      );
    }
  }, [createAccountSuccess]);

  useEffect(() => {
    if (isSuccess) {
      onCloseStatementPanel();
    }
  }, [isSuccess]);

  return {
    showCurrencies,
    setShowCurrencies,
    setPopupVisible,
    onBlockTopup,
    onBlockWithdraw,
    onClose,
    closeInProgress,
    openInProgress,
    blockCreditInProgress,
    unblockCreditInProgress,
    blockDebitInProgress,
    unblockDebitInProgress,
    setTopUpPanelIsOpen,
    closeIsSuccess,
    openIsSuccess,
    blockDebitIsSuccess,
    unblockDebitIsSuccess,
    blockCreditIsSuccess,
    unblockCreditIsSuccess,
    closeOpenError,
    blockUblockTopupError,
    blockUblockWithdrawalsError,
    setStatementPanelVisible,
    setSelectedAccount,
    setCurrenciesList,
    onUpdateVisibility,
    updateVisiblityInProgress,
    accountBalances,
    setBalances,
    defaultBalances,
    setDefaultBalances,
    onUpdateOverdraftBalanceValue,
    updateOverdraftBalanceIsLoading,
    topUpPanelIsOpen,
    selectedTopUpId,
    setSelectedTopUpId,
    setSelectedPrimaryAccount,
    setVisibleToClient,
    onCreateAccount,
    createAccountInProgress,
    popupVisible,
    primariesAccounts,
    selectedPrimaryAccount,
    visibleToClient,
    statementPanelVisible,
    onCloseStatementPanel,
    statementError,
    isLoading,
    isFetching,
    selectedCurrency,
    selectedDateType,
    selectedFormat,
    selectedDateRange,
    onDownloadStatement,
    currenciesList,
    setSelectedCurrency,
    setSelectedDateType,
    setSelectedDateRange,
    setSelectedFormat,
    selectedAccount,
  };
};

export default useCustomerAccounts;
